import React from 'react';

export const CONTACT_EMAIL = 'koheitamura15@gmail.com';

const SectionHeading = ({ as = 'h2', className = '', children }) =>
  React.createElement(as, { className }, children);

const BilingualText = ({ ja, en }) => (
  <span className="policy-bilingual">
    <span className="policy-ja">{ja}</span>
    <span className="policy-en">{en}</span>
  </span>
);

export default function LegalPolicyContent({
  headingAs = 'h2',
  headingClassName = '',
  mode = 'all',
}) {
  const showPrivacy = mode === 'all' || mode === 'privacy';
  const showTerms = mode === 'all' || mode === 'terms';

  return (
    <>
      {showPrivacy && (
        <>
          <p>
            <BilingualText
              ja="Chord Analyzerは、スコア保存機能の提供にあたりGoogleアカウント認証を使用します。以下に本サービスのプライバシーポリシーを示します。"
              en="Chord Analyzer uses Google account authentication to provide score-saving features. The following is the privacy policy for this service."
            />
          </p>

          <SectionHeading as={headingAs} className={headingClassName}>0. 運営者情報 / Operator Information</SectionHeading>
          <p>
            <BilingualText
              ja="サービス名: Chord Analyzer"
              en="Service Name: Chord Analyzer"
            />
            <BilingualText
              ja={<>お問い合わせ: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></>}
              en={<>Contact: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></>}
            />
          </p>

          <SectionHeading as={headingAs} className={headingClassName}>1. 取得する情報 / Information We Collect</SectionHeading>
          <p>
            <BilingualText
              ja="ログイン時にGoogleから取得する基本プロフィール情報（UID、メールアドレス、表示名、プロフィール画像）およびサービス利用により生成されるスコア情報（タイトル、アーティスト名、コード進行、更新日時）を取り扱います。"
              en="We handle basic profile information obtained from Google at login (UID, email address, display name, profile image), and score data generated through service usage (title, artist, chord progressions, update timestamps)."
            />
          </p>

          <SectionHeading as={headingAs} className={headingClassName}>2. Googleアカウント情報の利用範囲 / Scope of Google Account Data Usage</SectionHeading>
          <p>
            <BilingualText
              ja="Googleアカウント情報は、認証、ユーザー識別、保存データの紐付けのみに利用します。GmailやGoogle Driveなど他のGoogleサービス情報にはアクセスしません。"
              en="Google account data is used only for authentication, user identification, and linking saved data. We do not access other Google services data such as Gmail or Google Drive."
            />
          </p>

          <SectionHeading as={headingAs} className={headingClassName}>3. 利用目的 / Purpose of Use</SectionHeading>
          <p>
            <BilingualText
              ja="認証状態の維持、スコアの保存・取得、表示品質の改善、不正利用の防止のためにデータを利用します。"
              en="Data is used to maintain authentication state, save/retrieve scores, improve display quality, and prevent abuse."
            />
          </p>

          <SectionHeading as={headingAs} className={headingClassName}>4. 第三者提供 / Third-Party Provision</SectionHeading>
          <p>
            <BilingualText
              ja="法令に基づく場合を除き、取得した個人情報を第三者へ販売・提供しません。"
              en="Except where required by law, we do not sell or provide acquired personal information to third parties."
            />
          </p>

          <SectionHeading as={headingAs} className={headingClassName}>5. データ保存と削除 / Data Storage and Deletion</SectionHeading>
          <p>
            <BilingualText
              ja={<>保存データはユーザー自身でアプリ操作により削除できます。詳細は <a href="/data-deletion">Data Deletion Guide</a> をご確認ください。</>}
              en={<>Saved data can be deleted by the user through app operations. For details, see <a href="/data-deletion">Data Deletion Guide</a>.</>}
            />
            <BilingualText
              ja={<>Google連携は <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer">サードパーティ アクセス設定</a> から解除できます。</>}
              en={<>You can revoke Google linking from <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer">Third-party access settings</a>.</>}
            />
          </p>

          <SectionHeading as={headingAs} className={headingClassName}>6. ポリシーの変更 / Policy Changes</SectionHeading>
          <p>
            <BilingualText
              ja="本ポリシーは必要に応じて改定することがあります。重要な変更はサービス内で告知します。"
              en="This policy may be revised as necessary. Important changes will be announced within the service."
            />
            <BilingualText ja="施行日: 2025-01-01" en="Effective Date: 2025-01-01" />
            <BilingualText ja="最終更新日: 2026-03-08" en="Last Updated: 2026-03-08" />
          </p>
        </>
      )}

      {showTerms && (
        <>
          <p>
            <BilingualText
              ja="以下はChord Analyzerの利用規約です。"
              en="The following are the terms of service for Chord Analyzer."
            />
          </p>

          <SectionHeading as={headingAs} className={headingClassName}>1. アカウント / Account</SectionHeading>
          <p>
            <BilingualText
              ja="外部アカウント連携を利用する場合があります。保存情報にパスワードは含まれません。"
              en="External account linking may be used. Passwords are not included in the information we store."
            />
          </p>

          <SectionHeading as={headingAs} className={headingClassName}>2. ユーザー責任 / User Responsibility</SectionHeading>
          <p>
            <BilingualText
              ja="入力・保存する内容の権利処理を含め、ユーザーは自己の責任で本サービスを利用するものとします。"
              en="Users are responsible for using this service at their own risk, including rights processing for entered and saved content."
            />
          </p>

          <SectionHeading as={headingAs} className={headingClassName}>3. 禁止事項 / Prohibited Conduct</SectionHeading>
          <p>
            <BilingualText
              ja="法令違反、公序良俗違反、サービス運営妨害、なりすまし、他者情報の不正取得などを禁止します。"
              en="The following are prohibited: legal violations, public-order/morality violations, interference with service operation, impersonation, and unauthorized collection of others&apos; information."
            />
          </p>

          <SectionHeading as={headingAs} className={headingClassName}>4. サービス停止・変更 / Service Suspension/Changes</SectionHeading>
          <p>
            <BilingualText
              ja="保守・障害・災害その他の理由により、予告なくサービスを停止または変更することがあります。"
              en="This service may be suspended or changed without prior notice due to maintenance, failures, disasters, or other reasons."
            />
          </p>

          <SectionHeading as={headingAs} className={headingClassName}>5. 利用制限 / Usage Restrictions</SectionHeading>
          <p>
            <BilingualText
              ja="規約違反や不正利用が確認された場合、予告なく利用制限やアカウント削除を行うことがあります。"
              en="In cases of terms violations or fraudulent use, usage restrictions or account removal may be performed without prior notice."
            />
          </p>

          <SectionHeading as={headingAs} className={headingClassName}>6. 免責 / Disclaimer</SectionHeading>
          <p>
            <BilingualText
              ja="故意または重過失がある場合を除き、責任は通常生じうる範囲の損害に限られます。"
              en="Except in cases of intent or gross negligence, liability is limited to damages ordinarily foreseeable."
            />
          </p>

          <SectionHeading as={headingAs} className={headingClassName}>7. 規約の変更 / Changes to Terms</SectionHeading>
          <p>
            <BilingualText
              ja="本規約は必要に応じて変更されることがあります。変更後の規約はサービス内に掲示された時点で効力を生じます。"
              en="These terms may be changed as necessary. Revised terms take effect upon posting within the service."
            />
          </p>

          <SectionHeading as={headingAs} className={headingClassName}>8. 準拠法・裁判管轄 / Governing Law and Jurisdiction</SectionHeading>
          <p>
            <BilingualText
              ja="本規約は日本法に準拠し、本サービスに関する紛争は当方所在地を管轄する裁判所を第一審の専属的合意管轄裁判所とします。"
              en="These terms are governed by the laws of Japan. Any dispute related to this service shall be subject to the exclusive jurisdiction of the court with jurisdiction over our business location as the court of first instance."
            />
            <BilingualText ja="施行日: 2025-01-01" en="Effective Date: 2025-01-01" />
            <BilingualText ja="最終更新日: 2026-03-08" en="Last Updated: 2026-03-08" />
          </p>
        </>
      )}

      <SectionHeading as={headingAs} className={headingClassName}>お問い合わせ / Contact</SectionHeading>
      <p>
        <BilingualText
          ja={<>お問い合わせ: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></>}
          en={<>Contact: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></>}
        />
      </p>
    </>
  );
}
